import { useState } from 'react'
import {
  ActionIcon,
  Badge,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core'
import {
  IconCheck,
  IconPencil,
  IconX,
} from '@tabler/icons-react'

import {
  Valve,
  VALVE_MANUFACTURERS,
  VALVE_SIZES,
  ValveManufacturer,
  ValveSettingsField,
  ValveSettingsPatch,
  ValveSize,
} from '@data/device/model/device'

import {
  STATE_ICON,
  VALVE_STATE_META,
} from './valveStateMeta'
import { useValveEditPermissions } from './useValveEditPermissions'

// A draft field is absent when unchanged and null when explicitly cleared, so `undefined`
// and `null` cannot be collapsed here.
const draftValue = <F extends ValveSettingsField>(
  valve: Valve,
  draft: ValveSettingsPatch | undefined,
  field: F,
): ValveSettingsPatch[F] | null =>
  draft?.[field] !== undefined ? draft[field] : valve[field] ?? null

export interface ValveIdentitySettingsProps {
  valve: Valve
  // Needed for the permission check: the USER scope only grants edits on devices in an
  // account or location the user administers.
  device: {
    accountId: string
    deviceLocationId: string
  }
  draft?: ValveSettingsPatch
  onSettingsChange: (valve: Valve, patch: ValveSettingsPatch) => void
  isSaving?: boolean
  isSettingsUnavailable?: boolean
}

// The identity + settings portion shared by the Device Modal's valve card (settings only)
// and the Operate Valves page's valve card (settings + open/close). The caller supplies
// its own Card/Stack wrapper and any rows of its own — this renders two Group rows meant
// to sit inside that Stack.
export const ValveIdentitySettings = ({
                                        valve,
                                        device,
                                        draft,
                                        onSettingsChange,
                                        isSaving = false,
                                        isSettingsUnavailable = false,
                                      }: ValveIdentitySettingsProps) => {
  // Resolved here rather than passed in as props, because this card is shared by the
  // Device Modal and the Operate Valves page and both must gate identically. Manufacturer
  // and size stay visible when not editable — they are worth reading, and hiding them
  // would make an admin's and a user's view of the same valve differ in shape.
  const {
    canEditName: canEditValveName,
    canEditHardware: canEditValveHardware,
  } = useValveEditPermissions(device)

  const meta = VALVE_STATE_META[valve.state ?? 7]
  const icon = STATE_ICON[valve.state ?? 7]

  const name = draftValue(valve, draft, 'name')
  const manufacturer = draftValue(valve, draft, 'manufacturer')
  const size = draftValue(valve, draft, 'size')
  const isModified = !!draft

  const [isEditing, setIsEditing] = useState(false)
  const [draftName, setDraftName] = useState(name ?? '')

  const handleEditStart = () => {
    setDraftName(name ?? '')
    setIsEditing(true)
  }

  const handleSave = () => {
    onSettingsChange(valve, { name: draftName.trim() || null })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setDraftName(name ?? '')
    setIsEditing(false)
  }

  return (
    <>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm">
          <ThemeIcon
            variant="light"
            color={meta.color}
            size="lg"
            radius="xl"
          >
            {icon}
          </ThemeIcon>

          <Stack gap={0}>
            {isEditing ? (
              <Group gap="xs" wrap="nowrap">
                <TextInput
                  size="xs"
                  value={draftName}
                  onChange={(e) => setDraftName(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave()
                    if (e.key === 'Escape') handleCancel()
                  }}
                  placeholder={valve.key}
                  autoFocus
                  disabled={isSaving}
                />
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={handleSave}
                  loading={isSaving}
                >
                  <IconCheck size={14} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Text fw={600}>
                  {name ? `${valve.key} - ${name}` : valve.key}
                </Text>
                {canEditValveName && !isSettingsUnavailable && (
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={handleEditStart}
                  >
                    <IconPencil size={12} />
                  </ActionIcon>
                )}
              </Group>
            )}

            <Text size="sm" c="dimmed">
              Valve Status
            </Text>
          </Stack>
        </Group>

        <Group gap="xs" wrap="nowrap">
          {isModified && (
            <Badge color="yellow" variant="light" size="sm">
              Unsaved
            </Badge>
          )}

          <Badge
            color={meta.color}
            variant="light"
            leftSection={icon}
          >
            {meta.label}
          </Badge>
        </Group>
      </Group>

      <Group grow align="flex-start">
        <Select
          label="Manufacturer"
          placeholder={canEditValveHardware ? 'Not set' : '—'}
          size="xs"
          data={VALVE_MANUFACTURERS}
          value={manufacturer}
          onChange={(value) => onSettingsChange(valve, { manufacturer: value as ValveManufacturer | null })}
          disabled={isSaving || isSettingsUnavailable || !canEditValveHardware}
          clearable={!isSettingsUnavailable && canEditValveHardware}
        />
        <Select
          label="Size"
          placeholder={canEditValveHardware ? 'Not set' : '—'}
          size="xs"
          data={VALVE_SIZES}
          value={size}
          onChange={(value) => onSettingsChange(valve, { size: value as ValveSize | null })}
          disabled={isSaving || isSettingsUnavailable || !canEditValveHardware}
          clearable={!isSettingsUnavailable && canEditValveHardware}
        />
      </Group>
    </>
  )
}
